import React, { Fragment, useRef, useEffect, useState } from "react";
import "./ContactAjov.css";
import { ShineBorder } from "../../shadcomponents/ui/shine-border";
import { AJOVShimmerButton } from "../../shadcomponents/ui/ajov-shimmer-button";

const BASIN_URL = "https://usebasin.com/f/934deaa00854";

type Props = {
  hashedpage: string;
};
export enum VisibleStatus { //Need to do this for string issue assignment with css
  Visible = "visible",
  Hidden = "hidden",
}

type Errors = {
  full_name?: string;
  email?: string;
  message?: string;
};

const ContactAjov: React.FC<Props> = ({ hashedpage }) => {
  const [full_name, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [visible, setVisible] = useState(VisibleStatus.Hidden);
  const [showPort, setShowPort] = useState("");

  useEffect(() => {
    if (hashedpage === "#contact") {
      setVisible(VisibleStatus.Visible);
      setShowPort("show");
    }
  }, [hashedpage]);

  const clickContact = () => {
    window.location.hash = "#";
    setVisible(VisibleStatus.Hidden);
    setShowPort("");
  };

  const validate = () => {
    const errors: Errors = {};
    if (!full_name.trim()) {
      errors.full_name = "Name is required";
    }
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = "Invalid email address";
    }
    if (!message.trim()) {
      errors.message = "Message is required";
    } else if (message.trim().length < 5) {
      errors.message = "Message must be at least 5 characters";
    }
    return errors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (name === "full_name") setFullName(value);
    if (name === "email") setEmail(value);
    if (name === "message") setMessage(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    setError("");
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      const formData = new FormData();
      formData.append("full_name", full_name);
      formData.append("email", email);
      formData.append("message", message);

      const res = await fetch(BASIN_URL, {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        setFullName("");
        setEmail("");
        setMessage("");
        setSuccess(true);
        setError("");
      } else {
        setError("Submission failed. Please try again.");
        setSuccess(false);
      }
    } catch {
      setError("Submission failed. Please try again.");
      setSuccess(false);
    }
    setSubmitting(false);
  };

  return (
    <Fragment>
      <div
        className={`offcanvas offcanvas-end fixed bottom-0 flex flex-col max-w-full bg-white invisible bg-clip-padding shadow-sm outline-none transition duration-300 ease-in-out text-gray-700 top-0 right-0 border-none w-4/12 xs:w-full ${showPort}`}
        style={{ visibility: visible }}
        id="offcanvasRight"
        aria-labelledby="offcanvasRightLabel"
      >
        <div className="offcanvas-header flex items-center justify-between p-4">
          <h5
            className="offcanvas-title mb-0 leading-normal font-semibold"
            id="offcanvasRightLabel"
          >
            Contact Me
          </h5>
          <button
            type="button"
            className="btn-close box-content w-4 h-4 p-2 -my-5 -mr-2 text-black border-none rounded-none opacity-50 focus:shadow-none focus:outline-none focus:opacity-100 hover:text-black hover:opacity-75 hover:no-underline"
            data-bs-dismiss="offcanvas"
            onClick={clickContact}
            aria-label="Close"
          ></button>
        </div>
        <div className="offcanvas-body flex-grow p-4 overflow-y-auto">
          {/* ...SVGs and layout above... */}
          <section className="relative z-10">
            <div className="container">
              <div className="flex justify-center items-center mt-2">
                <div className="w-full px-4">
                  <div className="bg-white/80 relative rounded p-4 sm:p-8 shadow-lg">
                    <ShineBorder
                      shineColor={["#53B9E9", "#0094D9", "#2D338C"]}
                      borderWidth={4}
                      duration={8}
                    />

                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                      Let’s Build Something That Works
                    </h2>
                    <p className="text-md text-left leading-relaxed mb-3">
                      Whether you're launching a new product, optimizing
                      workflows, or integrating AI — you're in the right place.
                      I focus on{" "}
                      <span className="font-semibold ">cost-effective</span>,
                      scalable development that delivers results: from
                      full-stack apps and Shopify customizations to API
                      integrations and{" "}
                      <span className="font-semibold ">
                        gamified user experiences
                      </span>
                      .
                    </p>
                    <p className="text-left text-sm mb-3">
                      <span className="font-semibold">
                        Have a project in mind or just exploring?{" "}
                      </span>
                      <br />
                      Send a message below — responses are quick and
                      straightforward.
                    </p>

                    {/* Alert messages above the form */}
                    {error && (
                      <div className="mb-4 w-full rounded bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-center font-semibold">
                        {error}
                      </div>
                    )}
                    {success && (
                      <div className="mb-4 w-full rounded bg-green-100 border border-green-400 text-green-700 px-4 py-3 text-center font-semibold">
                        Thank you! Your message has been sent.
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="mb-6">
                        <input
                          type="text"
                          name="full_name"
                          placeholder="Your Name"
                          value={full_name}
                          onChange={handleChange}
                          className="w-full rounded border-blue-300 py-3 px-[14px] text-body-color text-base border-2  outline-none focus-visible:shadow-none focus:border-blue-700"
                        />
                        {errors.full_name && (
                          <div className="text-red-600 text-sm">
                            {errors.full_name}
                          </div>
                        )}
                      </div>
                      <div className="mb-6">
                        <input
                          type="email"
                          name="email"
                          placeholder="Your Email"
                          value={email}
                          onChange={handleChange}
                          className="w-full rounded border-blue-300 py-3 px-[14px] text-body-color text-base border-2 outline-none focus-visible:shadow-none focus:border-blue-700"
                        />
                        {errors.email && (
                          <div className="text-red-600 text-sm">
                            {errors.email}
                          </div>
                        )}
                      </div>
                      <div className="mb-6">
                        <textarea
                          name="message"
                          rows={6}
                          placeholder="Your Message"
                          value={message}
                          onChange={handleChange}
                          className="w-full rounded  border-blue-300 py-3 px-[14px] text-body-color text-base border-2 resize-none outline-none focus-visible:shadow-none focus:border-blue-700"
                        ></textarea>
                        {errors.message && (
                          <div className="text-red-600 text-sm">
                            {errors.message}
                          </div>
                        )}
                      </div>

                      <AJOVShimmerButton
                        type="submit"
                        shimmerColor="#ffffff"
                        shimmerDuration="1s"
                        shimmerSize="0.21em"
                        borderRadius="0.5rem"
                        background="rgb(29 78 216)"
                        className="w-full p-3 transition hover:opacity-90 disabled:opacity-50"
                        disabled={submitting}
                      >
                        {submitting ? "Sending..." : "Send Message"}
                      </AJOVShimmerButton>
                    </form>
                    {/* ...SVGs and layout below... */}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Fragment>
  );
};

export default ContactAjov;
